import { useCallback, useEffect, useRef, useState } from 'react';
import { Joyride, ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import IconActionButton from './ui/IconActionButton';
import Modal from './modals/Modal';
import ModalActions from './modals/ModalActions';
import { buildTourSteps, celebrateCompletion, findFirstVisibleIndex, waitFor, waitForTarget } from './onboardingTourSteps';

function OnboardingTourTooltip({ step, primaryProps, tooltipProps, isLastStep, onRequestQuit }) {
  return (
    <div {...tooltipProps} className="relative bg-white overflow-hidden rounded-2xl p-6 shadow-xl max-w-sm">
      <button
        type="button"
        className="group absolute top-3 right-3 p-1 border-0 bg-transparent cursor-pointer focus:outline-none"
        aria-label="Quit tour"
        onClick={onRequestQuit}
      >
        <XMarkIcon className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors" aria-hidden="true" />
      </button>
      <p className="text-sm text-slate-700 pr-5">{step.content}</p>
      <div className="flex justify-end gap-2 mt-6">
        <button
          {...primaryProps}
          title={undefined}
          type="button"
          className="btn btn-primary btn-sm w-24 focus:outline-none"
        >
          {isLastStep ? 'Done' : 'Next'}
        </button>
      </div>
    </div>
  );
}

function OnboardingTour({
  isAdmin,
  isSuperadmin,
  effectiveOrgId,
  orgName,
  hasSeenOnboarding,
  updateProfile,
  activeTab,
  setActiveTab,
  setIsOrgBioModalOpen,
  setSelectedVenueId,
  setTourTargetShowId,
  venueSectionApiRef,
  showsSectionApiRef,
  dataSectionApiRef,
}) {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isQuitConfirmOpen, setIsQuitConfirmOpen] = useState(false);
  const stepsRef = useRef([]);
  const contextRef = useRef({});
  const hasAutoStartedRef = useRef(false);

  const buildSteps = useCallback(
    () =>
      buildTourSteps({
        isSuperadmin,
        contextRef,
        setIsOrgBioModalOpen,
        setSelectedVenueId,
        setTourTargetShowId,
        setActiveTab,
        venueSectionApiRef,
        showsSectionApiRef,
        dataSectionApiRef,
      }),
    [
      isSuperadmin,
      setIsOrgBioModalOpen,
      setSelectedVenueId,
      setTourTargetShowId,
      setActiveTab,
      venueSectionApiRef,
      showsSectionApiRef,
      dataSectionApiRef,
    ]
  );

  const persistCompletion = useCallback(async () => {
    try {
      await updateProfile?.({ has_seen_onboarding: true });
    } catch (err) {
      console.error('Failed to persist onboarding completion:', err);
    }
  }, [updateProfile]);

  const start = useCallback(async () => {
    contextRef.current = {};
    const list = buildSteps();
    stepsRef.current = list;
    const idx = await findFirstVisibleIndex(list, 0);
    if (idx === -1) return;
    await waitForTarget(list[idx].target);
    setStepIndex(idx);
    setRun(true);
  }, [buildSteps]);

  const advance = useCallback(async currentIndex => {
    const list = stepsRef.current;
    try {
      await list[currentIndex]?.onExit?.();
    } catch (err) {
      console.error('Onboarding tour: step action failed', err);
    }

    const nextIdx = await findFirstVisibleIndex(list, currentIndex + 1);
    if (nextIdx === -1) {
      setRun(false);
      await persistCompletion();
      return;
    }

    await waitForTarget(list[nextIdx].target);
    if (nextIdx === list.length - 1) celebrateCompletion();
    setStepIndex(nextIdx);
  }, [persistCompletion]);

  const handleJoyrideEvent = useCallback(data => {
    const { action, status, type, index } = data;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      persistCompletion();
      return;
    }

    if ((type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) || type === EVENTS.TARGET_NOT_FOUND) {
      advance(index);
    }
  }, [advance, persistCompletion]);

  useEffect(() => {
    if (hasAutoStartedRef.current) return;
    if (!isAdmin) return;
    if (hasSeenOnboarding === true) return;
    if (!effectiveOrgId || !orgName) return;
    if (activeTab !== 'manage') return;

    let cancelled = false;
    (async () => {
      const ready = await waitFor(
        () => {
          const venues = venueSectionApiRef.current?.getActiveVenues();
          return venues !== null && venues !== undefined;
        },
        10000
      );
      if (cancelled || hasAutoStartedRef.current) return;
      if (!ready) return;
      hasAutoStartedRef.current = true;
      start();
    })();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, hasSeenOnboarding, effectiveOrgId, orgName, activeTab, venueSectionApiRef, start]);

  const closeTourModals = useCallback(() => {
    setIsOrgBioModalOpen(false);
    venueSectionApiRef.current?.closeVenueModal();
    showsSectionApiRef.current?.closeShowModal();
  }, [setIsOrgBioModalOpen, venueSectionApiRef, showsSectionApiRef]);

  const handleQuitConfirmed = useCallback(() => {
    closeTourModals();
    setIsQuitConfirmOpen(false);
    setRun(false);
    persistCompletion();
  }, [closeTourModals, persistCompletion]);

  const handleReplay = useCallback(() => {
    closeTourModals();
    hasAutoStartedRef.current = true;
    setRun(false);
    start();
  }, [closeTourModals, start]);

  return (
    <>
      <IconActionButton
        label="Replay tour"
        icon={QuestionMarkCircleIcon}
        tooltipPosition="bottom"
        className=""
        onClick={handleReplay}
      />
      <Joyride
        run={run}
        stepIndex={stepIndex}
        steps={stepsRef.current}
        continuous
        onEvent={handleJoyrideEvent}
        tooltipComponent={props => (
          <OnboardingTourTooltip {...props} onRequestQuit={() => setIsQuitConfirmOpen(true)} />
        )}
        options={{
          zIndex: 10000,
          primaryColor: '#000000',
          overlayColor: 'rgba(0, 0, 0, 0.3)',
          spotlightRadius: 0,
          overlayClickAction: false,
          skipBeacon: true,
          targetWaitTimeout: 3000,
        }}
      />

      {/* z-[10001] since Joyride's own overlay sits at zIndex 10000 below it. */}
      <Modal
        isOpen={isQuitConfirmOpen}
        onClose={() => setIsQuitConfirmOpen(false)}
        title="Quit the tour?"
        maxWidth="max-w-md"
        zIndex="z-[10001]"
      >
        <p className="text-sm text-slate-400">
          You can replay it any time from the ? icon next to your organization name.
        </p>
        <ModalActions
          cancelLabel="Keep Going"
          onCancel={() => setIsQuitConfirmOpen(false)}
          confirmLabel="Quit"
          onConfirm={handleQuitConfirmed}
        />
      </Modal>
    </>
  );
}

export default OnboardingTour;
